import { formatCss, interpolate } from 'culori';

import type { DeckHistory, DeckHistoryCard } from './deck-page.ts';
import { historyGeometry, stableCardRotation } from './deck-history.ts';

export const PROGRESS_PAGE_SIZE = 8;
export const PROGRESS_PAGE_COLUMNS = 2;
export const PROGRESS_PAGE_ROWS = 4;
export const PROGRESS_GREEN_MAX_DAYS = 7;
export const PROGRESS_RECENT_MAX_DAYS = 30;
export const PROGRESS_AGING_MAX_DAYS = 105;
export const PROGRESS_UNREVIEWED_MAX_DAYS = 180;
export const PROGRESS_DAY_MS = 24 * 60 * 60 * 1000;

export type ProgressPalette = {
  background: string;
  border: string;
  success: string;
  successSurface: string;
  recent: string;
  recentSurface: string;
  aging: string;
  agingSurface: string;
  destructive: string;
  errorSurface: string;
};

export type ProgressMarkColors = {
  fill: string;
  stroke: string;
};

export type ProgressLegendItem = {
  colors: ProgressMarkColors;
  id: 'fresh' | 'recent' | 'aging' | 'over180' | 'never';
};

export const defaultProgressPalette: ProgressPalette = {
  background: '#f6f3eb',
  border: '#8f877a',
  success: '#1e704f',
  successSurface: '#e3f0e7',
  recent: '#ad7900',
  recentSurface: '#fff1a8',
  aging: '#c95b16',
  agingSurface: '#ffd8b8',
  destructive: '#9b2c22',
  errorSurface: '#f5e5e0',
};

export type DeckProgressCard = {
  card: DeckHistoryCard;
  colors: ProgressMarkColors;
  latestCorrectAt: number | null;
  rotation: number;
};

export type DeckProgressPage = {
  cards: Array<DeckProgressCard | null>;
};

export type DeckProgressTopic = {
  id: string;
  name: string;
  pages: DeckProgressPage[];
  slug: string;
  unlocked?: boolean;
};

export type DeckProgressUnit = {
  id: string;
  name: string;
  slug: string;
  topics: DeckProgressTopic[];
  unlocked?: boolean;
};

export type DeckProgressSection = {
  id: string;
  name: string;
  slug: string;
  units: DeckProgressUnit[];
  unlocked?: boolean;
};

function sortedById<T extends { id: string }>(items: T[]): T[] {
  return [...items].sort((a, b) => a.id.localeCompare(b.id));
}

export function progressMarkColors(
  latestCorrectAt: number | null,
  now: number,
  palette: ProgressPalette = defaultProgressPalette,
): ProgressMarkColors {
  const fresh = { fill: palette.successSurface, stroke: palette.success };
  const recent = { fill: palette.recentSurface, stroke: palette.recent };
  const aging = { fill: palette.agingSurface, stroke: palette.aging };
  const unreviewed = {
    fill: palette.errorSurface,
    stroke: palette.destructive,
  };
  const never = { fill: palette.background, stroke: palette.border };
  if (latestCorrectAt === null) return never;
  const ageDays = Math.max(0, now - latestCorrectAt) / PROGRESS_DAY_MS;
  if (ageDays <= PROGRESS_GREEN_MAX_DAYS) {
    return fresh;
  }
  if (ageDays <= PROGRESS_RECENT_MAX_DAYS) {
    const position =
      (ageDays - PROGRESS_GREEN_MAX_DAYS) /
      (PROGRESS_RECENT_MAX_DAYS - PROGRESS_GREEN_MAX_DAYS);
    return interpolateProgressPair(fresh, recent, position);
  }
  if (ageDays <= PROGRESS_AGING_MAX_DAYS) {
    const position =
      (ageDays - PROGRESS_RECENT_MAX_DAYS) /
      (PROGRESS_AGING_MAX_DAYS - PROGRESS_RECENT_MAX_DAYS);
    return interpolateProgressPair(recent, aging, position);
  }
  if (ageDays < PROGRESS_UNREVIEWED_MAX_DAYS) {
    const position =
      (ageDays - PROGRESS_AGING_MAX_DAYS) /
      (PROGRESS_UNREVIEWED_MAX_DAYS - PROGRESS_AGING_MAX_DAYS);
    return interpolateProgressPair(aging, unreviewed, position);
  }
  return unreviewed;
}

function interpolateProgressPair(
  from: ProgressMarkColors,
  to: ProgressMarkColors,
  position: number,
): ProgressMarkColors {
  if (position <= 0) return from;
  if (position >= 1) return to;
  return {
    fill: formatCss(interpolate([from.fill, to.fill], 'oklch')(position)),
    stroke: formatCss(interpolate([from.stroke, to.stroke], 'oklch')(position)),
  };
}

export function progressLegend(
  now: number,
  palette: ProgressPalette = defaultProgressPalette,
): ProgressLegendItem[] {
  return [
    {
      colors: progressMarkColors(now, now, palette),
      id: 'fresh',
    },
    {
      colors: progressMarkColors(
        now - PROGRESS_RECENT_MAX_DAYS * PROGRESS_DAY_MS,
        now,
        palette,
      ),
      id: 'recent',
    },
    {
      colors: progressMarkColors(
        now - PROGRESS_AGING_MAX_DAYS * PROGRESS_DAY_MS,
        now,
        palette,
      ),
      id: 'aging',
    },
    {
      colors: progressMarkColors(
        now - (PROGRESS_UNREVIEWED_MAX_DAYS + 1) * PROGRESS_DAY_MS,
        now,
        palette,
      ),
      id: 'over180',
    },
    {
      colors: progressMarkColors(null, now, palette),
      id: 'never',
    },
  ];
}

export function progressPaletteFromCss(): ProgressPalette {
  if (typeof document === 'undefined') return defaultProgressPalette;
  const styles = getComputedStyle(document.documentElement);
  const read = (name: string, fallback: string) =>
    styles.getPropertyValue(name).trim() || fallback;
  return {
    background: read('--background', defaultProgressPalette.background),
    border: read('--border', defaultProgressPalette.border),
    success: read('--success', defaultProgressPalette.success),
    successSurface: read(
      '--success-surface',
      defaultProgressPalette.successSurface,
    ),
    recent: read('--progress-recent', defaultProgressPalette.recent),
    recentSurface: read(
      '--progress-recent-surface',
      defaultProgressPalette.recentSurface,
    ),
    aging: read('--progress-aging', defaultProgressPalette.aging),
    agingSurface: read(
      '--progress-aging-surface',
      defaultProgressPalette.agingSurface,
    ),
    destructive: read('--destructive', defaultProgressPalette.destructive),
    errorSurface: read('--error-surface', defaultProgressPalette.errorSurface),
  };
}

export function buildDeckProgressMap(
  history: DeckHistory,
  now: number,
  palette: ProgressPalette = defaultProgressPalette,
): DeckProgressSection[] {
  const latestCorrect = new Map<string, number>();
  for (const event of history.events) {
    if (event.result !== 'correct') continue;
    const previous = latestCorrect.get(event.cardId);
    if (previous === undefined || event.occurredAt > previous) {
      latestCorrect.set(event.cardId, event.occurredAt);
    }
  }
  const cardsByTopic = new Map<string, DeckHistoryCard[]>();
  for (const card of history.cards) {
    if (!card.active) continue;
    const cards = cardsByTopic.get(card.topicId) ?? [];
    cards.push(card);
    cardsByTopic.set(card.topicId, cards);
  }
  const topicsByUnit = new Map<string, typeof history.topics>();
  for (const topic of sortedById(history.topics)) {
    const topics = topicsByUnit.get(topic.unitId) ?? [];
    topics.push(topic);
    topicsByUnit.set(topic.unitId, topics);
  }
  const unitsBySection = new Map<string, typeof history.units>();
  for (const unit of sortedById(history.units)) {
    const units = unitsBySection.get(unit.sectionId) ?? [];
    units.push(unit);
    unitsBySection.set(unit.sectionId, units);
  }
  return sortedById(history.sections)
    .map((section) => {
      const units = (unitsBySection.get(section.id) ?? [])
        .map((unit) => {
          const topics = (topicsByUnit.get(unit.id) ?? [])
            .map((topic) => {
              const cards = (cardsByTopic.get(topic.id) ?? []).sort((a, b) =>
                a.id.localeCompare(b.id),
              );
              const pages: DeckProgressPage[] = [];
              for (
                let offset = 0;
                offset < cards.length;
                offset += PROGRESS_PAGE_SIZE
              ) {
                const pageCards = cards
                  .slice(offset, offset + PROGRESS_PAGE_SIZE)
                  .map((card) => {
                    const latestCorrectAt = latestCorrect.get(card.id) ?? null;
                    return {
                      card,
                      colors: progressMarkColors(latestCorrectAt, now, palette),
                      latestCorrectAt,
                      rotation: stableCardRotation(card.id),
                    } satisfies DeckProgressCard;
                  });
                pages.push({
                  cards: [
                    ...pageCards,
                    ...Array.from(
                      {
                        length: PROGRESS_PAGE_SIZE - pageCards.length,
                      },
                      () => null,
                    ),
                  ],
                });
              }
              return {
                id: topic.id,
                name: topic.name,
                pages,
                slug: topic.slug,
                unlocked: topic.unlocked,
              };
            })
            .filter((topic) => topic.pages.length > 0);
          return {
            id: unit.id,
            name: unit.name,
            slug: unit.slug,
            topics,
            unlocked: unit.unlocked,
          };
        })
        .filter((unit) => unit.topics.length > 0);
      return {
        id: section.id,
        name: section.name,
        slug: section.slug,
        units,
        unlocked: section.unlocked,
      };
    })
    .filter((section) => section.units.length > 0);
}

export const progressGeometry = {
  ...historyGeometry,
  pageColumns: PROGRESS_PAGE_COLUMNS,
  pageRows: PROGRESS_PAGE_ROWS,
  pagePadding: 2,
  pageGap: 2,
  pageRadius: historyGeometry.markRadius + 2,
};
